/**
 * Google Analytics Tracking Hook
 *
 * Client-side event tracking using gtag.js
 */

import { useEffect, useCallback } from 'react';
import { GoogleAnalyticsConfig } from '../services/google-analytics.service';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

export function useGoogleAnalytics(config: GoogleAnalyticsConfig | null) {
  const isConfigured = !!(config && config.measurement_id);

  // Initialize gtag.js
  useEffect(() => {
    if (!isConfigured) {
      console.log('[GA] Not configured:', { config, isConfigured });
      return;
    }

    console.log('[GA] Initializing with measurement_id:', config.measurement_id);

    // Check if already loaded
    if (window.gtag) {
      console.log('[GA] Already loaded');
      return;
    }

    // Load gtag.js script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${config.measurement_id}`;

    script.onload = () => {
      console.log('[GA] Script loaded successfully');
    };

    script.onerror = () => {
      console.error('[GA] Failed to load script');
    };

    document.head.appendChild(script);

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer?.push(arguments);
    };

    console.log('[GA] Configuring gtag with:', config.measurement_id);

    // Configure gtag
    window.gtag('js', new Date());
    window.gtag('config', config.measurement_id, {
      send_page_view: false, // We'll handle this manually
      debug_mode: config.debug_mode || false,
    });

    return () => {
      // Cleanup
      const scripts = document.querySelectorAll(`script[src*="googletagmanager"]`);
      scripts.forEach(s => s.remove());
    };
  }, [isConfigured, config?.measurement_id, config?.debug_mode]);

  // Track event
  const trackEvent = useCallback((eventName: string, eventParams: Record<string, any> = {}) => {
    if (!window.gtag || !isConfigured) {
      console.log('[GA] Cannot track event:', eventName, '- gtag not ready');
      return;
    }

    console.log('[GA] Tracking event:', eventName, eventParams);
    window.gtag('event', eventName, eventParams);
  }, [isConfigured]);

  // Track form view
  const trackFormView = useCallback((formId: number, formTitle?: string) => {
    if (!config?.track_views) return;

    trackEvent('form_view', {
      form_id: formId,
      form_title: formTitle,
      event_category: 'easyforms',
      event_label: 'form_view',
    });
  }, [config?.track_views, trackEvent]);

  // Track form start
  const trackFormStart = useCallback((formId: number, formTitle?: string) => {
    if (!config?.track_starts) return;

    trackEvent('form_start', {
      form_id: formId,
      form_title: formTitle,
      event_category: 'easyforms',
      event_label: 'form_start',
    });
  }, [config?.track_starts, trackEvent]);

  // Track form submission
  const trackFormSubmission = useCallback((formId: number, submissionId?: number, formTitle?: string) => {
    if (!config?.track_submissions) return;

    trackEvent('form_submission', {
      form_id: formId,
      submission_id: submissionId,
      form_title: formTitle,
      event_category: 'easyforms',
      event_label: 'form_submission',
    });
  }, [config?.track_submissions, trackEvent]);

  // Track field interaction
  const trackFieldInteraction = useCallback((
    formId: number,
    fieldName: string,
    action: 'focus' | 'blur',
    formTitle?: string
  ) => {
    if (!config?.track_field_interactions) return;

    trackEvent('field_interaction', {
      form_id: formId,
      field_name: fieldName,
      interaction_type: action,
      form_title: formTitle,
      event_category: 'easyforms',
      event_label: 'field_interaction',
    });
  }, [config?.track_field_interactions, trackEvent]);

  // Track validation error
  const trackValidationError = useCallback((
    formId: number,
    fieldName: string,
    errorType: string,
    formTitle?: string
  ) => {
    if (!config?.track_validation_errors) return;

    trackEvent('validation_error', {
      form_id: formId,
      field_name: fieldName,
      error_type: errorType,
      form_title: formTitle,
      event_category: 'easyforms',
      event_label: 'validation_error',
    });
  }, [config?.track_validation_errors, trackEvent]);

  // Track form abandonment
  const trackFormAbandonment = useCallback((
    formId: number,
    completionPercentage: number,
    fieldsFilled: number,
    totalFields: number,
    formTitle?: string
  ) => {
    if (!config?.track_abandonment) return;

    trackEvent('form_abandonment', {
      form_id: formId,
      completion_percentage: completionPercentage,
      fields_filled: fieldsFilled,
      total_fields: totalFields,
      form_title: formTitle,
      event_category: 'easyforms',
      event_label: 'form_abandonment',
    });
  }, [config?.track_abandonment, trackEvent]);

  return {
    trackEvent,
    trackFormView,
    trackFormStart,
    trackFormSubmission,
    trackFieldInteraction,
    trackValidationError,
    trackFormAbandonment,
    isConfigured,
  };
}

